import { useState } from 'react';
import { isAxiosError } from 'axios';
import { CustomDatePicker } from '@/components/form/CustomDatePicker';
import { MediaField } from './MediaField';
import { BannerAudience } from './BannerAudience';
import {
  uploadBannerMedia,
  updateBannerMedia,
  type BannerMedia,
  type BannerRow,
  type BannerSegment,
  type SaveBannerPayload,
} from '@/api/banners';

/**
 * Форма баннера — поля 1:1 старый Orchid MarketingEditLayout: название,
 * подсказка, описание, ссылка, даты, медиафайл и включение. Плюс блок «Кому
 * показывать» (в Orchid — «сегменты» отдельной таблицей с промежуточными
 * экранами, см. докблок BannerAudience/BannerController).
 */
export function BannerForm({
  initial,
  onSave,
  onCancel,
}: {
  initial?: BannerRow;
  onSave: (payload: SaveBannerPayload) => Promise<void>;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [hint, setHint] = useState(initial?.hint ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [url, setUrl] = useState(initial?.url ?? '');
  const [dateFrom, setDateFrom] = useState(initial?.dateFrom ?? '');
  const [dateTo, setDateTo] = useState(initial?.dateTo ?? '');
  const [active, setActive] = useState(initial?.active ?? false);
  const [media, setMedia] = useState<BannerMedia | null>(initial?.media ?? null);
  const [segments, setSegments] = useState<BannerSegment[]>(initial?.segments ?? []);
  const [fieldErrors, setFieldErrors] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const invalid = (field: string) => (fieldErrors.includes(field) ? ' edit-field--invalid' : '');

  const submit = async () => {
    // Обязательно только название — 1:1 валидация MarketingEditScreen::save().
    const missing: string[] = [];
    if (!name.trim()) missing.push('name');

    let message: string | null = null;
    if (missing.length > 0) message = 'Заполните обязательные поля.';
    else if (dateFrom && dateTo && dateFrom > dateTo) {
      missing.push('dateFrom');
      message = 'Дата начала не может быть позже даты завершения.';
    } else if (segments.some((s) => s.type === 'test' && !s.data)) {
      message = 'В правиле «Работающие с тестом» не выбран тест.';
    }

    setFieldErrors(missing);
    if (message) {
      setError(message);
      return;
    }

    setError(null);
    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        hint: hint.trim() || null,
        description: description.trim() || null,
        url: url.trim() || null,
        dateFrom: dateFrom || null,
        dateTo: dateTo || null,
        active,
        mediaId: media?.id ?? null,
        segments,
      });
    } catch (err) {
      const detail = isAxiosError<{ message?: string }>(err) ? err.response?.data?.message : null;
      setError(detail || 'Не удалось сохранить баннер.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={`operator-template-form${saving ? ' operator-template-form--saving' : ''}`}
      aria-busy={saving}
    >
      {error && <p className="field-error">{error}</p>}

      <div className="edit-fields">
        <label className={`edit-field${invalid('name')}`}>
          <span>Название *</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={saving}
          />
        </label>

        <label className="edit-field">
          <span>Подсказка</span>
          <input
            type="text"
            value={hint}
            onChange={(e) => setHint(e.target.value)}
            disabled={saving}
          />
        </label>

        <label className="edit-field edit-field--checkbox edit-field--full">
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
            disabled={saving}
          />
          <span>Включён</span>
        </label>

        <label className="edit-field edit-field--full">
          <span>Описание</span>
          <textarea
            rows={5}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={saving}
          />
        </label>

        <label className="edit-field edit-field--full">
          <span>Ссылка</span>
          <input
            type="text"
            value={url}
            placeholder="https://example.ru/link"
            onChange={(e) => setUrl(e.target.value)}
            disabled={saving}
          />
        </label>

        <div className={`edit-field${invalid('dateFrom')}`}>
          <span>Дата начала</span>
          <CustomDatePicker value={dateFrom} onChange={setDateFrom} />
        </div>

        <div className="edit-field">
          <span>Дата завершения</span>
          <CustomDatePicker value={dateTo} onChange={setDateTo} />
        </div>

        <div className="edit-field edit-field--full">
          <span>Медиафайл</span>
          <MediaField
            media={media}
            onChange={setMedia}
            disabled={saving}
            onUpload={uploadBannerMedia}
            onUpdateParams={updateBannerMedia}
          />
        </div>

        <div className="edit-field edit-field--full">
          <span>Кому показывать</span>
          <BannerAudience segments={segments} onChange={setSegments} disabled={saving} />
        </div>
      </div>

      <div className="operator-template-form__actions">
        <button type="button" className="btn btn--secondary" onClick={onCancel} disabled={saving}>
          Отмена
        </button>
        <button type="button" className="btn btn--primary" onClick={submit} disabled={saving}>
          Сохранить
        </button>
      </div>
    </div>
  );
}
